/**
 * Custom hook for managing intake data and mutations
 */

import { useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { groupIntakeByCategory, getMealCategoryTotals } from "../utils";
import type { IntakeFormData, IntakeByCategory } from "../types";

interface UseIntakeDataProps {
  selectedKlientId: number | null;
  selectedDate: string;
}

export function useIntakeData({
  selectedKlientId,
  selectedDate,
}: UseIntakeDataProps) {
  // Queries
  const { data: klienti = [] } = trpc.klient.list.useQuery();
  const { data: prijemKategorie = [] } = trpc.prijemKategoria.list.useQuery();
  const { data: potraviny = [] } = trpc.potravina.list.useQuery();

  const { data: prijem = [], isLoading } =
    trpc.prijem.getByKlientAndDate.useQuery(
      { klientId: selectedKlientId!, datum: selectedDate },
      { enabled: !!selectedKlientId }
    );

  const { data: dailyTotals } = trpc.prijem.getDailyTotals.useQuery(
    { klientId: selectedKlientId!, datum: selectedDate },
    { enabled: !!selectedKlientId }
  );

  // Mutations
  const utils = trpc.useUtils();
  const createMutation = trpc.prijem.create.useMutation();
  const updateMutation = trpc.prijem.update.useMutation();
  const deleteMutation = trpc.prijem.delete.useMutation();

  // Computed values
  const prijemByKategoria: IntakeByCategory = useMemo(() => {
    return groupIntakeByCategory(prijem, prijemKategorie);
  }, [prijem, prijemKategorie]);

  // Filter clients with nutrition subscription
  const nutritionKlienti = useMemo(() => {
    return klienti.filter(k => k.odberStrava);
  }, [klienti]);

  // Handlers
  const createIntake = async (data: IntakeFormData) => {
    try {
      await createMutation.mutateAsync(data);
      toast.success("Food added to intake");
      utils.prijem.getByKlientAndDate.invalidate();
      utils.prijem.getDailyTotals.invalidate();
      return true;
    } catch (error) {
      toast.error("Failed to add food");
      return false;
    }
  };

  const updateIntake = async (id: number, data: Partial<IntakeFormData>) => {
    try {
      await updateMutation.mutateAsync({ id, data });
      toast.success("Food intake updated");
      utils.prijem.getByKlientAndDate.invalidate();
      utils.prijem.getDailyTotals.invalidate();
      return true;
    } catch (error) {
      toast.error("Failed to update food intake");
      return false;
    }
  };

  const deleteIntake = async (id: number) => {
    if (!confirm("Are you sure you want to remove this food from intake?")) {
      return false;
    }

    try {
      await deleteMutation.mutateAsync({ id });
      toast.success("Food removed from intake");
      utils.prijem.getByKlientAndDate.invalidate();
      utils.prijem.getDailyTotals.invalidate();
      return true;
    } catch (error) {
      toast.error("Failed to remove food");
      return false;
    }
  };

  const getMealTotals = (kategoriaId: number) => {
    const items = prijemByKategoria[kategoriaId] || [];
    return getMealCategoryTotals(items);
  };

  return {
    // Data
    klienti: nutritionKlienti,
    prijemKategorie,
    potraviny,
    prijem,
    prijemByKategoria,
    dailyTotals,
    isLoading,

    // Methods
    createIntake,
    updateIntake,
    deleteIntake,
    getMealTotals,
  };
}
